import { ingredientToOption } from "@/lib/add-ingredient-utilities";
import {
  ShapedClass,
  dot,
  searchClasses,
  shapeClass,
  shapeClassLinear,
} from "@/lib/class-utilities";
import {
  combineUnits,
  getBaseUnit,
  getUnitOptions,
  getUnitOrFallback,
  stringIsValidNumber,
  stringToNumber,
  toAbsoluteAmount,
  toRelativeAmount,
  toRelativePrice,
  unitToOption,
} from "@/lib/measurement";
import {
  TagOptionType,
  classNames,
  ellipsise,
  isAdmin,
  isAssistant,
  isStudent,
  isTeacher,
  searchTags,
  sortByFn,
} from "@/lib/util";
import {
  AncillariesComponent,
  IngredientQuery,
  IngredientsQuery,
  OrderCarouselQuery,
  PriceWarning_Range,
  PriceWarning_Type,
  Recipe,
  RecipeQuery,
  Recipe_MadeInGroups,
  useCreateManyRecipesMutation,
  useDeleteRecipeMutation,
  useUpdateRecipeMutation,
} from "@/queries/autogenerate/graphql";
import { Field, Label, Switch } from "@headlessui/react";
import {
  BanknotesIcon,
  CheckCircleIcon,
  CheckIcon,
  CpuChipIcon,
  DocumentDuplicateIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  FolderIcon,
  LinkIcon,
  LockClosedIcon,
  LockOpenIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  PencilIcon,
  PlusCircleIcon,
  PlusIcon,
  PrinterIcon,
  ShoppingCartIcon,
  TrashIcon,
  XCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import cuid from "cuid";
import { format, formatDistanceToNow } from "date-fns";
import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import toast from "react-hot-toast";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import AsyncCreatableSelect from "react-select/async-creatable";
import { AsyncSelectIngredient } from "../AsyncSelectIngredient";
import { InformationCircleComponent } from "../Commons";
import CreateIngredients from "../CreateIngredients";
import DialogComponent from "../Dialog";
import DragNDrop from "../DragNDrop";
import ErrorMessage from "../ErrorMessage";
import IngredientsGridItem from "../IngredientsGridItem";
import { CurrentUser, OrderDialogContext, UserContext } from "../Page";
import GetLatestOrderSessions from "../QueryHelpers/GetLatestOrderSessions";
import GetRecipe from "../QueryHelpers/GetRecipe";
import TextEditor from "../TextEditor";
import { InventoryGrid } from "../styles/Layout";
import { TextContentWrapper } from "../styles/TextContentWrapper";
import Tooltip from "../styles/Tooltip";
import ExploreIcon from "../svg/ExploreIcon";
import {
  HeadlessRecipeImage,
  MainRecipeActions,
  RecipeSectionComponent,
} from "./RecipesGridItem";
import {
  getPathToFolder,
  getRecipeIngredients,
  getRecipeSections,
} from "./util";

interface Props {
  recipe: OrderCarouselQuery["recipes"][0];
  selectedRecipes: string[];
  setSelectedRecipes: Dispatch<SetStateAction<string[]>>;
  folder:
    | NonNullable<CurrentUser["school"]>["recipeCollections"][0]
    | null
    | undefined;
  sortedFolders: NonNullable<CurrentUser["school"]>["recipeCollections"];
  search: string;
}

const RecipesListItem: React.FC<Props> = ({
  recipe,
  selectedRecipes,
  setSelectedRecipes,
  folder,
  sortedFolders,
  search,
}) => {
  const [archived, setArchived] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const [{ fetching, error }, updateRecipe] = useUpdateRecipeMutation();

  return archived ? null : (
    <UserContext.Consumer>
      {(user) => (
        <>
          <tr
            key={recipe.id}
            className={
              selectedRecipes.includes(recipe.id) ? "bg-gray-50" : "bg-white"
            }
          >
            <td className="relative px-7 min-sm:w-12 min-sm:px-6">
              {selectedRecipes.includes(recipe.id) && (
                <div className="absolute inset-y-0 left-0 w-0.5 bg-blue-600" />
              )}
              <input
                type="checkbox"
                className="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600"
                value={recipe.id}
                checked={selectedRecipes.includes(recipe.id)}
                onChange={(e) =>
                  setSelectedRecipes(
                    e.target.checked
                      ? [...selectedRecipes, recipe.id]
                      : selectedRecipes.filter((id) => id !== recipe.id)
                  )
                }
              />
            </td>
            <td
              className={classNames(
                "max-w-[12rem] truncate whitespace-nowrap py-4 pr-3 text-sm font-medium",
                selectedRecipes.includes(recipe.id)
                  ? "text-blue-600"
                  : "text-gray-900"
              )}
            >
              <div className="flex items-center gap-2">
                <div className="w-10 text-center">
                  {recipe.image ? (
                    <div
                      className="h-10 w-10 rounded-full bg-cover bg-center shadow-lg"
                      style={{
                        backgroundImage: `url(${recipe.image})`,
                      }}
                    />
                  ) : (
                    <>
                      <div className="flex h-10 w-10 items-center justify-center rounded-full border border-solid border-cyan-300 bg-gradient-to-tr from-cyan-100 to-green-100 opacity-50 shadow-lg transition-all group-hover:opacity-100" />
                    </>
                  )}
                </div>
                <div className="flex flex-col gap-1 truncate">
                  <span title={recipe.name} className="truncate">
                    {recipe.name}
                  </span>
                  <span className="font-normal text-gray-400">
                    Serves {recipe.servings} &middot;{" "}
                    {recipe.user?.name || "Unknown"}
                  </span>
                  {search ? (
                    <div className="flex items-center gap-2 rounded border border-blue-400 border-opacity-50 bg-blue-50 p-1 text-sm">
                      <FolderIcon className="w-5 text-blue-400" />{" "}
                      {getPathToFolder(
                        sortedFolders.find(
                          (f) => f.id == recipe.recipeCollection?.id
                        ),
                        sortedFolders
                      )
                        .map(
                          (id) =>
                            sortedFolders.find((c) => c.id === id)?.name ||
                            "unknown folder"
                        )
                        .join(" / ")}
                    </div>
                  ) : null}
                </div>
              </div>
            </td>
            {/* <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
          {recipe.servings}
        </td> */}
            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
              {recipe.classes?.length ? (
                <div className="flex flex-wrap items-center">
                  {recipe.classes.map((t) => (
                    <span key={t.id} className="tag tag-cyan">
                      {t.name}
                    </span>
                  ))}
                </div>
              ) : null}
            </td>
            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
              {recipe.tags?.length ? (
                <div className="flex flex-wrap items-center">
                  {recipe.tags.map((t) => (
                    <span key={t.id} className="tag">
                      {t.name}
                    </span>
                  ))}
                </div>
              ) : null}
            </td>
            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
              {format(new Date(recipe.createdAt), "dd/MM/yy")}
            </td>
            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
              {format(new Date(recipe.updatedAt), "dd/MM/yy")}
            </td>
            <td className="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium min-sm:pr-3">
              {/* Actions etc. */}
              <div className="flex items-center justify-end gap-2">
                <a
                  href={`https://recipe.classcart.com/?id=${recipe.id}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Tooltip content="View Printable Page">
                    <button className="">
                      <PrinterIcon className="w-5 text-cyan-700 hover:text-cyan-900" />
                    </button>
                  </Tooltip>
                </a>

                {user.school &&
                !(isStudent(user) && !user.school.permMakeOrders) ? (
                  <OrderDialogContext.Consumer>
                    {({ setOrderDialog, setRecipeToOrder }) => (
                      <Tooltip content="Order This Recipe">
                        <button
                          onClick={() => {
                            setRecipeToOrder(recipe.id);
                            setOrderDialog(true);
                          }}
                        >
                          <ShoppingCartIcon className="w-5 text-green-600 hover:text-green-700" />
                        </button>
                      </Tooltip>
                    )}
                  </OrderDialogContext.Consumer>
                ) : null}

                <DeleteRecipe
                  recipe={recipe}
                  user={user}
                  setDeleted={setDeleted}
                  setSelectedRecipes={setSelectedRecipes}
                />

                {recipe.locked ? (
                  isTeacher(user) ? (
                    <Tooltip
                      content={
                        <div className="font-normal">
                          <h2 className="mb-2 text-white">
                            This Recipe Is Locked
                          </h2>
                          <p>Press this button to unlock it for students.</p>
                        </div>
                      }
                    >
                      <button
                        className=""
                        disabled={fetching}
                        onClick={async () => {
                          await updateRecipe({
                            where: { id: recipe.id },
                            data: {
                              locked: { set: false },
                            },
                          });
                        }}
                      >
                        {fetching ? (
                          <div className="spinner h-5 w-5" />
                        ) : (
                          <LockClosedIcon className="w-5 text-gray-800 hover:text-gray-900" />
                        )}
                      </button>
                    </Tooltip>
                  ) : (
                    <Tooltip
                      content={
                        <div className="font-normal">
                          <h2 className="mb-2 text-white">
                            This Recipe Is Locked
                          </h2>
                          <p>
                            Locked recipes are read only and cannot be edited by
                            students.
                          </p>
                        </div>
                      }
                    >
                      <LockClosedIcon className="w-5 text-gray-800 hover:text-gray-900" />
                    </Tooltip>
                  )
                ) : isTeacher(user) ? (
                  <Tooltip
                    content={
                      <div className="font-normal">
                        <h2 className="mb-2 text-white">Lock This Recipe</h2>
                        <p>
                          Locking a recipe makes it read only for students and
                          prevents them making changes. They can still duplicate
                          the recipe.
                        </p>
                      </div>
                    }
                  >
                    <button
                      disabled={fetching}
                      onClick={async () => {
                        await updateRecipe({
                          where: { id: recipe.id },
                          data: {
                            locked: { set: true },
                          },
                        });
                      }}
                    >
                      {fetching ? (
                        <div className="spinner h-5 w-5" />
                      ) : (
                        <LockOpenIcon className="w-5 text-gray-800 hover:text-gray-900" />
                      )}
                    </button>
                  </Tooltip>
                ) : null}

                <button
                  className="ml-4 text-blue-600 hover:text-blue-900"
                  onClick={() => setExpanded((exp) => !exp)}
                >
                  {expanded ? "Less" : "More"}
                  <span className="sr-only">, {recipe.name}</span>
                </button>
              </div>
            </td>
            {/* <td className="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium min-sm:pr-3">
              
            </td> */}
          </tr>
          {expanded ? (
            <tr>
              <td colSpan={7}>
                <div className="bg-gray-100 p-4 min-sm:p-6">
                  <GetRecipe id={recipe.id} spinner={true}>
                    {(recipe) =>
                      recipe ? (
                        <ExpandedItem
                          recipe={recipe}
                          user={user}
                          setDeleted={setDeleted}
                          setArchived={setArchived}
                          folder={folder}
                          sortedFolders={sortedFolders}
                          setSelectedRecipes={setSelectedRecipes}
                        />
                      ) : (
                        <p>Recipe Not Found</p>
                      )
                    }
                  </GetRecipe>
                </div>
              </td>
            </tr>
          ) : null}
        </>
      )}
    </UserContext.Consumer>
  );
};

interface ExpandedItemProps {
  recipe: NonNullable<RecipeQuery["recipe"]>;
  user: CurrentUser;
  setDeleted: React.Dispatch<React.SetStateAction<boolean>>;
  setArchived: React.Dispatch<React.SetStateAction<boolean>>;
  folder:
    | NonNullable<CurrentUser["school"]>["recipeCollections"][0]
    | null
    | undefined;
  sortedFolders: NonNullable<CurrentUser["school"]>["recipeCollections"];
  setSelectedRecipes: Dispatch<SetStateAction<string[]>>;
}

const ExpandedItem: React.FC<ExpandedItemProps> = ({
  recipe,
  user,
  setDeleted,
  setArchived,
  folder,
  sortedFolders,
  setSelectedRecipes,
}) => {
  const [newName, setNewName] = useState(recipe.name);
  const [firstNameUpdate, setFirstNameUpdate] = useState(true);
  // Auto-save using a debounced function
  useEffect(() => {
    if (firstNameUpdate) {
      console.log("crisis averted");
      setFirstNameUpdate(false);
      return;
    }

    const handler = setTimeout(async () => {
      console.log("auto-update");

      await updateRecipe({
        where: { id: recipe.id },
        data: { name: { set: newName } },
      });
      onUpdateRecipeCompleted();
    }, 500);

    // Return a cleanup function that will be called every ...
    // ... time useEffect is re-called.
    // If it is re-called before the timeout, the timeout will be cleared ...
    // ... thus debouncing.
    return () => {
      clearTimeout(handler);
    };
  }, [newName]);

  const [isChangingServings, setServingsChange] = useState(false);
  const [servings, setServings] = useState(recipe.servings);

  const [madeInGroupsValue, setMadeInGroupsValue] =
    useState<Recipe_MadeInGroups | null>(recipe.madeInGroups || null);

  const [isAddingSection, setAddingSection] = useState(false);
  const [sectionName, setSectionName] = useState("");

  const [isChangingMethod, setMethodChange] = useState(false);
  // const [method, setMethod] = useState(recipe.method || "");

  const [isAddingInfo, setAddingInfo] = useState(false);
  const [info, setInfo] = useState("");

  const [isAddingClass, setAddClass] = useState(false);
  const [newClass, setNewClass] = useState<ShapedClass | null>(null);

  const [isAddingTag, setAddTag] = useState(false);
  const [newTags, setNewTags] = useState<readonly TagOptionType[]>([]);

  const [isAddingIngredient, setAddingIngredient] = useState(false);
  const [newIngredient, setNewIngredient] = useState<
    IngredientsQuery["ingredients"][number] | null
  >(null);

  // const newIngredient = ingredients.find((i) => i.id == newIngredientID);

  const [newIngredientAmount, setNewIngredientAmount] = useState<
    number | string
  >(1);
  const [newIngredientUnit, setNewIngredientUnit] = useState<
    IngredientsQuery["ingredients"][number]["units"][number] | null
  >(null);
  const [newIngredientPreparation, setNewIngredientPreparation] = useState("");

  const [editDialog, setEditDialog] = useState(false);
  const open = () => setEditDialog(true);
  const close = () => setEditDialog(false);

  const [reorderPulse, setReorderPulse] = useState(false);

  const [isCreatingIngredients, setCreatingIngredients] = useState(false);

  const [{ fetching: fetchingOne, error: errorOne }, updateRecipe] =
    useUpdateRecipeMutation();
  const [{ fetching: fetchingTwo, error: errorTwo }, createManyRecipes] =
    useCreateManyRecipesMutation();

  const fetching = fetchingOne || fetchingTwo;
  const error = errorOne || errorTwo;

  // Check if the unit still exists on the ingredient
  const unitExists = newIngredient
    ? combineUnits(newIngredient).find((cu) => cu.id === newIngredientUnit?.id)
    : undefined;

  const recipeSections = getRecipeSections(recipe);
  const UUIDfy = useMemo(
    () => (recipeSections: ReturnType<typeof getRecipeSections>) =>
      recipeSections.map((rs) => ({
        ...rs,
        uuid: rs.id,
        recipeIngredients: rs.recipeIngredients.map((ri) => ({
          ...ri,
          uuid: ri.id,
        })),
      })),
    []
  );

  const onUpdateRecipeCompleted = () => {
    setAddingSection(false);
    setSectionName("");

    setServingsChange(false);
    setMethodChange(false);

    setAddingInfo(false);
    setInfo("");

    setAddClass(false);
    setNewClass(null);

    setAddTag(false);
    setNewTags([]);
  };

  const addIngredientInput = useRef<HTMLInputElement>(null);

  const cost =
    user.school?.plan !== "basic"
      ? +recipe.recipeIngredients
          .reduce(
            (acc, cur) =>
              acc +
              toRelativePrice(
                cur.ingredient.prices[0],
                { amount: recipe.servings * cur.amount },
                cur.ingredient
              ),
            0
          )
          .toFixed(2)
      : 0;

  const priceLimit = sortByFn(
    user.school?.priceWarnings.filter(
      (pw) => pw.type === PriceWarning_Type.Recipe
    ) || [],
    (pw) =>
      pw.range === PriceWarning_Range.Recipe
        ? pw.price / 100 / recipe.servings
        : pw.price / 100 / pw.amount
  )[0];
  const unitaryPriceLimit = priceLimit
    ? priceLimit.range === PriceWarning_Range.Recipe
      ? priceLimit.price / 100 / recipe.servings
      : priceLimit.price / 100 / priceLimit.amount
    : 0;

  const abovePriceLimit =
    cost && priceLimit && cost / recipe.servings > unitaryPriceLimit;

  const locked =
    isStudent(user) && (!user.school?.permModifyRecipes || recipe.locked);
  const canCreateIngredients = !(
    isStudent(user) && !user.school?.permCreateIngredients
  );

  const unsortedClasses =
    isAdmin(user) || isAssistant(user)
      ? user.school?.classes
      : user.school?.classes.filter((c) =>
          c.users.some((u) => u.id === user.id)
        );

  return (
    <div className="flex flex-row gap-8 rounded-lg bg-white px-4 py-4 xl:flex-col min-sm:px-6">
      {/* TOP */}
      <div className="flex flex-1 flex-col justify-start gap-2">
        <div
          className={classNames(
            "flex items-end",
            recipe.image ? "bg-cover bg-center p-2 pt-76" : ""
          )}
          style={
            recipe.image
              ? {
                  backgroundImage: `linear-gradient(to bottom, transparent, transparent 50%, white), url("${recipe.image}")`,
                  margin: "-10px 0 0 -10px",
                  width: "calc(100% + 10px + 10px)",
                  borderRadius: "6px",
                }
              : {}
          }
        >
          <MainRecipeActions
            user={user}
            recipe={recipe}
            setDeleted={setDeleted}
            setArchived={setArchived}
            sortedFolders={sortedFolders}
            folder={folder}
            locked={locked}
            recipeSections={recipeSections}
            isNative={true}
            setSelectedRecipes={setSelectedRecipes}
          />
        </div>
        {/* {recipe.image ? (
          <div
            className="h-76 w-76 rounded-lg bg-cover shadow-inner"
            style={{
              backgroundImage: `url(${recipe.image})`,
            }}
          />
        ) : null} */}
        <div className="shrink-0">
          {/* Recipe Name */}
          <div className="mb-2 flex items-center justify-between gap-2.5">
            <input
              type="text"
              value={newName}
              placeholder={"Recipe Name"}
              className="-ml-1.5 flex-1 truncate rounded border border-solid border-transparent p-1.5 text-2xl font-medium transition hover:border-gray-200 focus:border-gray-200"
              onChange={(e) => setNewName(e.target.value)}
              disabled={locked}
            />
            {fetching ? <div className="spinner" /> : null}
          </div>

          {/* Created / Updated / Last Ordered */}
          <div className="mb-2">
            <small className="mb-2 block font-normal">
              Created:{" "}
              {format(new Date(recipe.createdAt), "p EEEE LLLL do, yyyy")}
            </small>
            <small className="mb-2 block">
              Updated:{" "}
              {format(new Date(recipe.updatedAt), "p EEEE LLLL do, yyyy")}
            </small>
            <GetLatestOrderSessions recipeId={recipe.id}>
              {(orderSessions) =>
                orderSessions.length ? (
                  <>
                    {orderSessions.slice(0, 1).map((orderSession) => (
                      <Tooltip
                        content={format(
                          new Date(orderSession.createdAt),
                          "p EEEE LLLL do, yyyy"
                        )}
                        key={orderSession.id}
                      >
                        <small className="inline-block rounded border border-solid border-cyan-200 bg-cyan-50 px-1.5 py-0.5">
                          Last ordered{" "}
                          {formatDistanceToNow(
                            new Date(orderSession.createdAt),
                            {
                              addSuffix: true,
                            }
                          )}
                        </small>
                      </Tooltip>
                    ))}
                  </>
                ) : null
              }
            </GetLatestOrderSessions>
          </div>

          {/* Creation Process */}
          {recipe.process == "manual" ? (
            <div className="flex max-w-max items-center justify-center rounded-lg bg-slate-100  p-1.5 text-sm font-semibold">
              <PencilIcon className="mx-1 inline-block w-4 text-slate-500" />
              <span className="text-slate-900">Created Manually</span>
            </div>
          ) : recipe.process == "duplicated" ? (
            <div className="flex max-w-max items-center justify-center rounded-lg bg-slate-100  p-1.5 text-sm font-semibold">
              <DocumentDuplicateIcon className="mx-1 inline-block w-4 text-slate-500" />
              <span className="text-slate-900">Duplicated</span>
            </div>
          ) : recipe.process == "document" ? (
            <div className="flex max-w-max items-center justify-center rounded-lg bg-emerald-50 p-1.5 text-sm font-semibold">
              <DocumentTextIcon className="mx-1 inline-block w-4 text-emerald-500" />
              <span className="text-emerald-900">Uploaded from Document</span>
            </div>
          ) : recipe.process == "discovered" ? (
            <div className="flex max-w-max items-center justify-center rounded-lg bg-gradient-to-tr from-cyan-100 to-green-100 p-1.5 text-sm font-semibold">
              <MapPinIcon className="mx-1 inline-block w-4 text-cyan-500" />
              <span className="text-cyan-900">Discovered on ClassCart</span>
            </div>
          ) : recipe.process == "website" ? (
            <div className="flex max-w-max items-center justify-center rounded-lg bg-sky-50 p-1.5 text-sm font-semibold">
              <LinkIcon className="mx-1 inline-block w-4 text-sky-500" />
              <span className="text-sky-900">Uploaded from Website</span>
            </div>
          ) : recipe.process == "ai generated" ? (
            <div className="flex max-w-max items-center justify-center rounded-lg bg-cyan-50 p-1.5 text-sm font-semibold">
              <CpuChipIcon className="mx-1 inline-block w-4 text-cyan-500" />
              <span className="text-cyan-900">AI Generated</span>
            </div>
          ) : recipe.process == "searched website" ? (
            <div className="flex max-w-max items-center justify-center rounded-lg bg-amber-50 p-1.5 text-sm font-semibold">
              <MagnifyingGlassIcon className="mx-1 inline-block w-4 text-amber-500" />
              <span className="text-amber-900">Searched Recipe Website</span>
            </div>
          ) : null}

          {/* Source */}
          {recipe.source ? (
            <small className="mb-2 truncate">
              Source:{" "}
              <Tooltip content={recipe.source}>
                <a
                  href={recipe.source}
                  target="_blank"
                  rel="noreferrer"
                  className="truncate text-blue-500 hover:text-blue-700"
                >
                  {ellipsise(
                    recipe.source.replace(/^(https?:\/\/)?(www\.?)?/, ""),
                    50
                  )}
                </a>
              </Tooltip>
            </small>
          ) : null}

          {/* Connected Classes */}
          <div className="mb-2">
            {!isAddingClass &&
              (recipe.classes.length ? (
                recipe.classes.map((t) => (
                  <span
                    className="tag tag-cyan group/classtag inline-flex items-center gap-0 transition-all hover:gap-1.5"
                    key={t.id}
                  >
                    {t.name}
                    {!fetching && !locked ? (
                      <XMarkIcon
                        className="w-0 cursor-pointer group-hover/classtag:w-3"
                        onClick={() => {
                          updateRecipe({
                            where: { id: recipe.id },
                            data: {
                              classes: { disconnect: [{ id: t.id }] },
                            },
                          });
                          onUpdateRecipeCompleted();
                        }}
                      />
                    ) : null}
                  </span>
                ))
              ) : (
                <span className="tag bg-transparent text-gray-500">
                  No Classes
                </span>
              ))}
            {isAddingClass && (
              <>
                <AsyncSelect
                  className="radio-select w-screen max-w-xl"
                  classNamePrefix="react-select"
                  isSearchable={true}
                  value={newClass}
                  // defaultValue={unsortedClasses.map(shapeClassLinear).filter((c) => !recipe.classes.some((rc) => rc.id == c.obj.id))[0] || null}
                  defaultOptions
                  loadOptions={(inputValue, callback) => {
                    callback(
                      searchClasses(
                        inputValue,
                        unsortedClasses?.map(shapeClass) || []
                      )
                    );
                  }}
                  noOptionsMessage={() => "No Options"}
                  maxMenuHeight={200}
                  placeholder="Select a Class to Connect"
                  onChange={(data) => {
                    if (data?.value) {
                      setNewClass(shapeClassLinear(data.obj));
                    }
                  }}
                  // onChange={(newValue) => setNewClass(newValue || null)}
                  styles={{
                    container: (provided) => ({
                      ...provided,
                      position: "sticky",
                      top: "0px",
                      marginTop: "0",
                      zIndex: 20,
                    }),
                    control: (styles) => ({
                      ...styles,
                      backgroundColor: "white",
                    }),
                    option: (styles, { data }) => ({
                      ...styles,
                      ...dot(...data.colors),
                    }),
                    input: (styles) => ({
                      ...styles,
                      ...dot(...(newClass?.colors || [])),
                    }),
                    placeholder: (styles) => ({ ...styles, ...dot() }),
                    singleValue: (provided, { data }) => ({
                      ...provided,
                      ...dot(...data.colors),
                    }),
                  }}
                />
                {newClass ? (
                  <Tooltip content="Confirm">
                    <button
                      className=""
                      onClick={() => {
                        updateRecipe({
                          where: { id: recipe.id },
                          data: {
                            classes: {
                              connect: [{ id: newClass.obj.id }],
                            },
                          },
                        });
                        onUpdateRecipeCompleted();
                      }}
                    >
                      <CheckIcon className="icon icon-green" />
                    </button>
                  </Tooltip>
                ) : null}
              </>
            )}
            {locked ? null : (
              <Tooltip content={isAddingClass ? "Cancel" : "Connect a Class"}>
                <button
                  className=""
                  disabled={locked}
                  onClick={() => setAddClass(!isAddingClass)}
                >
                  {isAddingClass ? (
                    <XMarkIcon className="icon icon-gray" />
                  ) : (
                    <PlusIcon className="icon icon-gray" />
                  )}
                </button>
              </Tooltip>
            )}
          </div>

          {/* Connected Tags */}
          <div className="mb-2">
            {!isAddingTag &&
              (recipe.tags.length ? (
                recipe.tags.map((t) => (
                  <span
                    key={t.id}
                    className="tag group/tag inline-flex items-center gap-0 transition-all hover:gap-1.5"
                  >
                    {t.name}
                    {!fetching && !locked ? (
                      <XMarkIcon
                        className="w-0 cursor-pointer group-hover/tag:w-3"
                        onClick={() => {
                          updateRecipe({
                            where: { id: recipe.id },
                            data: {
                              tags: { disconnect: [{ id: t.id }] },
                            },
                          });
                          onUpdateRecipeCompleted();
                        }}
                      />
                    ) : null}
                  </span>
                ))
              ) : (
                <span className="tag text-gray-500">No Tags</span>
              ))}
            {isAddingTag && (
              <>
                <AncillariesComponent
                  variables={{ schoolID: user.school?.id || "" }}
                >
                  {({ error, fetching, data }) =>
                    error ? (
                      <ErrorMessage error={error} />
                    ) : fetching ? (
                      <div className="spinner" />
                    ) : !isStudent(user) || user.school?.permCreateTags ? (
                      <AsyncCreatableSelect
                        classNamePrefix="react-select"
                        placeholder="Search or Create"
                        isClearable
                        isMulti
                        isSearchable
                        autoFocus
                        noOptionsMessage={() => "No Options"}
                        value={newTags}
                        defaultOptions
                        loadOptions={(inputValue, callback) => {
                          callback(
                            searchTags(
                              inputValue,
                              data?.tags || [],
                              recipe.tags
                            )
                          );
                        }}
                        onCreateOption={(inputValue) => {
                          const newTag = {
                            value: cuid(),
                            label: inputValue,
                            __isNew__: true,
                            type: "tag",
                          } as TagOptionType;
                          setNewTags([...newTags, newTag]);
                        }}
                        onChange={(newValue) => setNewTags(newValue || [])}
                        maxMenuHeight={250}
                        styles={{
                          container: (provided) => ({
                            ...provided,
                            width: "180px",
                            display: "inline-block",
                          }),
                        }}
                      />
                    ) : (
                      <AsyncSelect
                        classNamePrefix="react-select"
                        placeholder="Search..."
                        isClearable
                        isMulti
                        isSearchable
                        autoFocus
                        value={newTags}
                        defaultOptions
                        loadOptions={(inputValue, callback) => {
                          callback(
                            searchTags(
                              inputValue,
                              data?.tags || [],
                              recipe.tags
                            )
                          );
                        }}
                        noOptionsMessage={() => "No Options"}
                        onChange={(newValue) => setNewTags(newValue || [])}
                        maxMenuHeight={250}
                        styles={{
                          container: (provided) => ({
                            ...provided,
                            width: "180px",
                            display: "inline-block",
                          }),
                        }}
                      />
                    )
                  }
                </AncillariesComponent>
                <Tooltip content="Confirm">
                  <button
                    className=""
                    onClick={() => {
                      updateRecipe({
                        where: { id: recipe.id },
                        data: {
                          tags: {
                            connect: newTags
                              .filter((t) => !t.__isNew__)
                              .map((t) => ({ id: t.value })),
                            create: newTags
                              .filter((t) => t.__isNew__)
                              .map((t) => ({
                                id: t.value,
                                name: t.label,
                                school: { connect: { id: user.school?.id } },
                              })),
                          },
                        },
                      });
                      onUpdateRecipeCompleted();
                    }}
                  >
                    <CheckIcon className="icon icon-green" />
                  </button>
                </Tooltip>
              </>
            )}
            {locked ? null : (
              <Tooltip content={isAddingTag ? "Cancel" : "Add a Tag"}>
                <button
                  className=""
                  disabled={locked}
                  onClick={() => setAddTag(!isAddingTag)}
                >
                  {isAddingTag ? (
                    <XMarkIcon className="icon icon-gray" />
                  ) : (
                    <PlusIcon className="icon icon-gray" />
                  )}
                </button>
              </Tooltip>
            )}
          </div>

          {/* Share Recipe */}
          {isTeacher(user) && !locked ? (
            <div className="my-6 flex items-center rounded border border-gray-100 bg-gray-50 p-2 shadow-inner">
              <Field className="flex w-full items-center justify-between">
                <Label className="flex flex-grow items-center" passive>
                  <ExploreIcon
                    className={classNames(
                      recipe.public ? "text-blue-500" : "text-gray-500",
                      "mr-2 w-6 transition-colors"
                    )}
                  />
                  <span className="flex flex-col">
                    <span className="text-lg font-medium text-gray-900">
                      {recipe.public
                        ? "This recipe is shared"
                        : "Share This Recipe"}
                    </span>
                    <span className="text-lg text-gray-500">
                      Other schools can use it for inspiration.
                      <Tooltip content="Shared recipes will be visible to other schools from the 'discover' page, and can also be used as a template when creating recipes.">
                        <span>
                          <InformationCircleComponent />
                        </span>
                      </Tooltip>
                    </span>
                  </span>
                </Label>
                <Switch
                  checked={recipe.public}
                  onChange={() => {
                    if (recipe.public || recipe.method) {
                      updateRecipe({
                        where: { id: recipe.id },
                        data: {
                          public: { set: !recipe.public },
                        },
                      });
                      onUpdateRecipeCompleted();
                    } else {
                      toast.error("Recipes cannot be shared without a method");
                    }
                  }}
                  className={classNames(
                    recipe.public ? "bg-blue-500" : "bg-gray-200",
                    "relative inline-flex h-5 w-8 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  )}
                >
                  <span className="sr-only">Share recipe</span>
                  <span
                    aria-hidden="true"
                    className={classNames(
                      recipe.public ? "translate-x-3" : "translate-x-0",
                      "pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                    )}
                  />
                </Switch>
              </Field>
            </div>
          ) : null}
        </div>
        <div className="shrink-0">
          {/* Made By */}
          {recipe.user ? (
            <div className="mb-2">
              <small>Made by: {recipe.user.name}</small>
            </div>
          ) : null}

          {/* Servings */}
          <div className="mb-2">
            <small>Makes {recipe.servings} Servings</small>
            {isChangingServings ? (
              <div>
                <input
                  type="number"
                  autoFocus
                  min={0}
                  onChange={(e) => setServings(parseFloat(e.target.value))}
                  value={servings}
                />
                {servings ? (
                  <button
                    className="my-0 px-1.5 py-0.5"
                    disabled={fetching}
                    onClick={() => {
                      updateRecipe({
                        where: { id: recipe.id },
                        data: {
                          servings: { set: servings },
                          recipeIngredients: {
                            update: recipe.recipeIngredients
                              .map((ingredient) => {
                                const {
                                  id,
                                  amount,
                                  unit,
                                  ingredient: {
                                    measurement,
                                    units: customUnits,
                                    density,
                                  },
                                } = ingredient;

                                const units = combineUnits({
                                  measurement,
                                  units: customUnits,
                                  density,
                                });

                                const appUnit = getUnitOrFallback(units, unit);
                                if (!appUnit) return null;
                                const relAmount = toRelativeAmount(
                                  amount,
                                  appUnit,
                                  density || undefined,
                                  recipe.servings
                                );

                                return {
                                  where: { id },
                                  data: {
                                    amount: {
                                      set: toAbsoluteAmount(
                                        relAmount,
                                        appUnit,
                                        density || undefined,
                                        servings
                                      ),
                                    },
                                  },
                                };
                              })
                              .filter(
                                (ri): ri is NonNullable<typeof ri> => !!ri
                              ),
                          },
                        },
                      });
                      onUpdateRecipeCompleted();
                    }}
                  >
                    <CheckIcon className="icon icon-green" />
                  </button>
                ) : null}
                <button className="" onClick={() => setServingsChange(false)}>
                  <XMarkIcon className="icon icon-gray" />
                </button>
              </div>
            ) : locked ? null : (
              <button
                className=""
                disabled={locked}
                onClick={() => setServingsChange(true)}
              >
                <PencilIcon className="icon icon-gray" />
              </button>
            )}
          </div>

          {/* Made In Groups */}
          <div className="mb-2">
            <small>
              To be made
              <select
                name="madeInGroups"
                id="madeIngroups"
                value={madeInGroupsValue || ""}
                onChange={(e) => {
                  const tempValue =
                    e.target.value == "unselected"
                      ? null
                      : (e.target.value as Recipe_MadeInGroups);

                  setMadeInGroupsValue(tempValue);

                  updateRecipe({
                    where: { id: recipe.id },
                    data: {
                      madeInGroups: {
                        set: tempValue || null,
                      },
                    },
                  });
                }}
                className="ml-1.5 rounded border border-solid border-gray-300 p-1.5"
                disabled={fetching}
              >
                <option value="unselected">(no option)</option>
                <option value={Recipe_MadeInGroups.Individuals}>
                  individually
                </option>
                <option value={Recipe_MadeInGroups.Pairs}>in pairs</option>
                <option value={Recipe_MadeInGroups.Trios}>
                  in groups of three
                </option>
                <option value={Recipe_MadeInGroups.Four}>
                  in groups of four
                </option>
                <option value={Recipe_MadeInGroups.Five}>
                  in groups of five
                </option>
                <option value={Recipe_MadeInGroups.Six}>
                  in groups of six
                </option>
              </select>
            </small>
            <small className="mt-1 block max-w-3xl rounded bg-blue-50 p-1.5 font-normal text-blue-600">
              Note: This does not affect orders, it is only for informational
              purposes. If you want to change how much this recipe produces,
              update the servings or the ingredients themselves.
            </small>
          </div>

          {/* Cost */}
          {cost > 0 ? (
            <small className="flex items-center">
              <BanknotesIcon className="mr-2 w-6 text-stone-600" />
              <div>
                <div
                  className={`m-1.5 font-medium ${
                    user.school?.fullRecipePosition == "right"
                      ? ""
                      : "inline-block"
                  }`}
                >
                  ${cost} for {recipe.servings} Servings
                </div>
                <div
                  className={`m-1.5 ${
                    user.school?.fullRecipePosition == "right"
                      ? ""
                      : "inline-block"
                  }`}
                >
                  (${(cost / recipe.servings).toFixed(2)} per Serving)
                </div>
                {abovePriceLimit ? (
                  <Tooltip
                    content={`This order is over the price limit. The limit is $${(
                      priceLimit.price / 100
                    ).toFixed(2)} per${
                      priceLimit.amount > 1 ? ` ${priceLimit.amount}` : ""
                    } ${priceLimit.range}${priceLimit.amount > 1 ? "s" : ""}${
                      priceLimit.amount > 1 ||
                      priceLimit.range === PriceWarning_Range.Recipe
                        ? ` ($${unitaryPriceLimit.toFixed(2)} per serving)`
                        : ""
                    }.`}
                  >
                    <span className="ml-1.5 inline-block">
                      <ExclamationTriangleIcon className="w-6 animate-wiggle text-orange-600" />
                    </span>
                  </Tooltip>
                ) : null}
              </div>
            </small>
          ) : null}
        </div>

        <HeadlessRecipeImage
          {...{
            recipe,
            fetching,
            user,
            updateRecipe,
            onUpdateRecipeCompleted,
          }}
        />
      </div>

      {/* <dl className="grid grid-cols-1 gap-x-4 gap-y-8 min-lg:grid-cols-2"> */}
      <dl className="flex min-w-118 flex-1 flex-col gap-8">
        {/* Ingredients */}
        <div className="min-sm:col-span-1">
          {/* <dt className="text-sm font-medium text-gray-500">Ingredients</dt>
          <dd className="mt-1 text-sm text-gray-900">
            Soon...
            <ul className="divide-y divide-gray-200 rounded-md border border-gray-200">
              {recipe.recipeIngredients.map((recipeIngredient) => (
                <li
                  key={recipeIngredient.id}
                  className="flex items-center justify-between py-3 pl-3 pr-4 text-sm"
                >
                  <div className="flex w-0 flex-1 items-center">
                    <span className="ml-2 w-0 flex-1 truncate">
                      {recipeIngredient.ingredient.name}
                    </span>
                  </div>
                  <div className="ml-4 flex-shrink-0">
                    <span className="font-medium text-gray-900">
                      {recipeIngredient.amount} {recipeIngredient.unit?.name}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </dd> */}
          <div>
            <div className="mt-8">
              {recipe.recipeIngredients.length ? (
                <DragNDrop
                  collection={UUIDfy(recipeSections)}
                  itemKey="recipeIngredients"
                  setCollection={async (newRecipeSections) => {
                    setReorderPulse(true);

                    const recipeIngredients =
                      getRecipeIngredients(newRecipeSections);

                    await updateRecipe({
                      where: { id: recipe.id },
                      data: {
                        recipeSections: {
                          update: newRecipeSections
                            .filter((rs) => rs.id !== "sectionLess")
                            .map((rs, index) => ({
                              where: { id: rs.id },
                              data: {
                                position: { set: index + 1 },
                              },
                            })),
                        },
                        recipeIngredients: {
                          update: recipeIngredients.map((ri, index) => {
                            // const oldRi = oldRecipeIngredients.find(
                            //   (ori) => ori.id === ri.id
                            // );

                            return {
                              where: { id: ri.id },
                              data: {
                                position: { set: index },
                                recipeSection: ri.recipeSection?.id
                                  ? {
                                      connect: {
                                        id: ri.recipeSection.id,
                                      },
                                    }
                                  : {
                                      disconnect: {
                                        id: { contains: "" }, // just get it to disconnect
                                      },
                                    },
                                // : oldRi?.recipeSection?.id
                                // ? {
                                //     // disconnect: true,
                                //     disconnect: {
                                //       id: { contains: "" }, // just get it to disconnect
                                //     },
                                //   }
                                // : undefined,
                              },
                            };
                          }),
                        },
                      },
                    });

                    setReorderPulse(false);
                  }}
                >
                  {(recipeSections) => (
                    <>
                      <h3 className="font-semibold">Ingredients</h3>
                      <div
                        className={classNames(
                          "space-y-1.5",
                          reorderPulse ? "animate-pulse" : ""
                        )}
                      >
                        {recipeSections.map((section, index) => (
                          <RecipeSectionComponent
                            key={section.id || "sectionLess"}
                            user={user}
                            index={index}
                            recipe={recipe}
                            section={section}
                            updateRecipe={updateRecipe}
                            fetching={fetching}
                          />
                        ))}
                      </div>
                      <div className="group -ml-3 flex max-w-xl items-center gap-2.5 pb-8 pt-3">
                        {isAddingSection ? (
                          <div className="flex items-center">
                            <input
                              type="text"
                              autoFocus
                              value={sectionName}
                              placeholder="Section Name"
                              onChange={(e) => setSectionName(e.target.value)}
                              className="rounded border border-solid border-gray-300 p-1.5"
                              disabled={fetching}
                            />
                            {sectionName ? (
                              <button
                                className="btn btn-primary"
                                onClick={async () => {
                                  await updateRecipe({
                                    where: { id: recipe.id },
                                    data: {
                                      recipeSections: {
                                        create: [
                                          {
                                            name: sectionName,
                                            position:
                                              recipe.recipeSections.length + 1,
                                          },
                                        ],
                                      },
                                    },
                                  });

                                  setAddingSection(false);
                                  setSectionName("");
                                }}
                                disabled={fetching}
                              >
                                <CheckCircleIcon className="mr-0.5 w-5 text-green-700" />
                                Confirm
                              </button>
                            ) : null}
                            <button
                              className="btn"
                              onClick={() => {
                                setAddingSection(false);
                                setSectionName("");
                              }}
                              disabled={fetching}
                            >
                              <XCircleIcon className="mr-0.5 w-5 text-gray-700" />
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            className="flex items-center gap-1"
                            onClick={() => setAddingSection(true)}
                          >
                            <PlusCircleIcon className="w-5 transform-gpu text-gray-700 transition group-hover:scale-125 group-hover:text-gray-900" />
                            <span className="font-semibold text-gray-700 transition group-hover:text-gray-800">
                              Add a Section
                            </span>
                          </button>
                        )}
                        <hr className="flex-1 border-gray-300" />
                      </div>
                    </>
                  )}
                </DragNDrop>
              ) : (
                <p>A recipe with no ingredients? How odd. 🤔</p>
              )}
              {isAddingIngredient ? (
                <form
                  className={classNames(
                    "group m-2.5 mx-auto flex w-full max-w-2xl flex-col items-center justify-center rounded-lg border border-solid border-cyan-100 p-2.5",
                    isCreatingIngredients
                      ? ""
                      : "bg-gradient-to-tr from-cyan-50 to-green-50"
                  )}
                  onSubmit={(e) => {
                    e.preventDefault();

                    console.log("There was a submit");

                    if (!newIngredient || !newIngredientUnit) {
                      return;
                    }

                    if (!unitExists) {
                      toast.error(
                        `The selected unit, "${newIngredientUnit.name}", does not exist on this ingredient. Please change the unit.`
                      );
                      return;
                    }

                    if (!stringIsValidNumber(String(newIngredientAmount))) {
                      toast.error(
                        "Please enter a valid amount. Accepted inputs can be a whole number, decimal or fraction."
                      );

                      return;
                    }

                    updateRecipe({
                      where: { id: recipe.id },
                      data: {
                        recipeIngredients: {
                          create: [
                            {
                              position:
                                getRecipeIngredients(recipeSections).length + 1,
                              amount: toAbsoluteAmount(
                                stringToNumber(String(newIngredientAmount)),
                                newIngredientUnit,
                                newIngredient.density || undefined,
                                recipe.servings
                              ),
                              ingredient: {
                                connect: {
                                  id: newIngredient.id,
                                },
                              },
                              unit: {
                                connect: {
                                  id: newIngredientUnit.id,
                                },
                              },
                              preparation: newIngredientPreparation,
                              recipeSection:
                                recipeSections.length > 1 &&
                                recipeSections[recipeSections.length - 1].id !==
                                  "sectionLess"
                                  ? {
                                      connect: {
                                        id: recipeSections[
                                          recipeSections.length - 1
                                        ].id,
                                      },
                                    }
                                  : undefined,
                            },
                          ],
                        },
                      },
                    });

                    onUpdateRecipeCompleted();

                    setAddingIngredient(false);
                    setNewIngredient(null);
                    setNewIngredientAmount(1);
                    setNewIngredientUnit(null);
                    setNewIngredientPreparation("");
                    setCreatingIngredients(false);
                  }}
                >
                  <div className="flex w-full items-center justify-between">
                    <AsyncSelectIngredient
                      value={
                        newIngredient ? ingredientToOption(newIngredient) : null
                      }
                      externalRef={addIngredientInput}
                      onChange={(value) => {
                        if (value) {
                          setNewIngredient(value);
                          setNewIngredientUnit(
                            getBaseUnit(
                              combineUnits(value),
                              value.selectionUnit || undefined
                            )
                          );
                        }
                      }}
                      onCreated={(ingredients) => {
                        setNewIngredient(ingredients[0]);
                        setNewIngredientUnit(
                          getBaseUnit(
                            combineUnits(ingredients[0]),
                            ingredients[0].selectionUnit || undefined
                          )
                        );
                      }}
                      placeholder="Apple..."
                      styles={{
                        menu: (provided) => ({
                          ...provided,
                          zIndex: 20,
                        }),
                      }}
                      className="flex-1"
                    />
                    {newIngredient ? (
                      <>
                        <Tooltip content="Edit Ingredient">
                          <button type="button" onClick={() => open()}>
                            <PencilIcon className="icon icon-gray opacity-25 group-hover:opacity-100" />
                          </button>
                        </Tooltip>
                        <DialogComponent
                          isOpen={editDialog}
                          onClose={close}
                          aria-label="Edit ingredient"
                        >
                          <h2 className="mb-8">Edit {newIngredient.name}</h2>
                          <InventoryGrid itemHeight="90px" itemWidth="400px">
                            <IngredientsGridItem
                              user={user}
                              ingredient={newIngredient}
                              isFragment={true}
                            />
                          </InventoryGrid>
                        </DialogComponent>
                      </>
                    ) : null}
                  </div>
                  {newIngredient ? (
                    <div className="w-full text-center">
                      <div className="flex w-full items-center gap-1.5 py-2">
                        <div className="relative">
                          <input
                            type="text"
                            onChange={(e) =>
                              setNewIngredientAmount(e.target.value)
                            }
                            value={newIngredientAmount ?? ""}
                            required={true}
                            className={classNames(
                              "w-12 rounded border p-2.5",
                              stringIsValidNumber(String(newIngredientAmount))
                                ? "border-gray-400"
                                : "border-orange-400 outline-orange-600"
                            )}
                            autoFocus
                          />
                          {stringIsValidNumber(
                            String(newIngredientAmount)
                          ) ? null : (
                            <Tooltip
                              content={`"${newIngredientAmount}" is not a valid input`}
                            >
                              <span>
                                <ExclamationTriangleIcon className="icon icon-orange absolute right-0 top-0 mt-6" />
                              </span>
                            </Tooltip>
                          )}
                        </div>
                        <Select
                          className="radio-select flex-1"
                          classNamePrefix="react-select"
                          defaultValue={unitToOption(
                            getBaseUnit(
                              combineUnits(newIngredient),
                              newIngredient.selectionUnit || undefined
                            ) as NonNullable<
                              IngredientQuery["ingredient"]
                            >["units"][number]
                          )}
                          options={getUnitOptions(
                            combineUnits(newIngredient),
                            newIngredient.density || undefined
                          )}
                          noOptionsMessage={() => "No Options"}
                          onChange={(data) => {
                            if (!data) return;

                            const tempUnit = combineUnits(newIngredient).find(
                              (u) => u.id === data.value
                            );
                            if (!tempUnit) return;

                            setNewIngredientUnit(tempUnit);
                          }}
                        />
                        {!unitExists ? (
                          <Tooltip
                            content={`The selected unit, "${newIngredientUnit?.name}", does not exist on this ingredient. Please change the unit.`}
                          >
                            <span>
                              <ExclamationTriangleIcon className="icon icon-orange" />
                            </span>
                          </Tooltip>
                        ) : null}
                      </div>
                      <input
                        type="text"
                        value={newIngredientPreparation}
                        onChange={(e) =>
                          setNewIngredientPreparation(e.target.value)
                        }
                        placeholder="grated, sliced..."
                        className="my-1.5 block rounded border border-gray-400 p-1.5"
                      />
                    </div>
                  ) : (
                    <div
                      style={{
                        textAlign: "center",
                        margin: "25px auto",
                      }}
                    >
                      {isCreatingIngredients ||
                      locked ||
                      !canCreateIngredients ? null : (
                        <button
                          disabled={locked}
                          onClick={() => setCreatingIngredients(true)}
                          className="btn"
                        >
                          Or Create a New One
                        </button>
                      )}
                      {isCreatingIngredients && user.school ? (
                        <div
                          className={classNames(
                            isCreatingIngredients
                              ? "my-2.5 rounded border border-solid border-cyan-200 bg-gradient-to-tr from-cyan-50 to-green-50 p-2.5"
                              : ""
                          )}
                        >
                          <button
                            disabled={locked}
                            onClick={() => {
                              setCreatingIngredients(false);

                              if (
                                addIngredientInput.current !=
                                document.activeElement
                              ) {
                                addIngredientInput.current?.focus();
                              }
                            }}
                            className="btn btn-warning"
                          >
                            Cancel
                          </button>
                          <CreateIngredients
                            school={user.school}
                            onCompleted={() => {
                              // remove the create ingredients section
                              setCreatingIngredients(false);

                              if (
                                addIngredientInput.current !=
                                document.activeElement
                              ) {
                                addIngredientInput.current?.focus();
                              }

                              // Let the user know: created but will take a while to show up in search
                              toast(
                                "The created ingredients may take a few seconds to show up in search results."
                              );
                            }}
                          />
                        </div>
                      ) : null}
                    </div>
                  )}
                  <div className="mt-5">
                    {newIngredient ? (
                      <button
                        type="submit"
                        disabled={fetching}
                        className="btn btn-primary"
                      >
                        <CheckCircleIcon className="mr-0.5 w-5 text-green-800" />{" "}
                        Confirm
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="btn"
                      onClick={() => {
                        setAddingIngredient(false);
                        setNewIngredient(null);
                        setNewIngredientAmount(1);
                        setNewIngredientUnit(null);
                        setNewIngredientPreparation("");
                      }}
                    >
                      <XCircleIcon className="mr-0.5 w-5 text-gray-900" />{" "}
                      Cancel
                    </button>
                  </div>
                </form>
              ) : locked ? null : (
                <button
                  className="btn"
                  disabled={locked}
                  onClick={() => setAddingIngredient(true)}
                >
                  Add an Ingredient
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Method */}
        <div className="min-sm:col-span-1">
          {/* <dt className="text-sm font-medium text-gray-500">Steps</dt>
          <dd className="mt-1 text-sm text-gray-900">
            Soon...
            <ol className="border border-gray-200 rounded-md divide-y divide-gray-200">
                      {recipe.steps.map((step) => (
                        <li
                          key={step.id}
                          className="pl-3 pr-4 py-3 flex items-center justify-between text-sm"
                        >
                          <div className="w-0 flex-1 flex items-center">
                            <span className="ml-2 flex-1 w-0 truncate">
                              {step.description}
                            </span>
                          </div>
                          <div className="ml-4 flex-shrink-0">
                            <span className="font-medium text-gray-900">
                              {step.time} min
                            </span>
                          </div>
                        </li>
                      ))}
                    </ol>
          </dd> */}
          <div>
            <div className="flex flex-row items-center justify-between">
              <h3>Method</h3>
              {isChangingMethod || locked ? null : (
                <Tooltip content="Edit Method">
                  <button
                    className="my-0 px-1.5 py-0.5"
                    disabled={locked}
                    onClick={() => setMethodChange(true)}
                  >
                    <PencilIcon className="w-5 text-gray-800" />
                  </button>
                </Tooltip>
              )}
            </div>
            {isChangingMethod ? (
              <TextEditor
                content={
                  recipe.method
                    ?.split(/\n/)
                    .map((value, index, arr) =>
                      arr.length > 1 ? `<p>${value}</p>` : value
                    )
                    .join("") || ""
                }
                onConfirm={(content) => {
                  updateRecipe({
                    where: { id: recipe.id },
                    data: {
                      method: { set: content || null },
                    },
                  });
                  onUpdateRecipeCompleted();
                }}
                onCancel={() => setMethodChange(false)}
              />
            ) : (
              <TextContentWrapper
                className="markdown rounded-lg border border-solid border-gray-300 bg-gray-100 p-1.5 shadow-lg"
                dangerouslySetInnerHTML={{
                  __html:
                    recipe.method
                      ?.split(/\n/)
                      .map((value, index, arr) =>
                        arr.length > 1 ? `<p>${value}</p>` : value
                      )
                      .join("") || "No Method",
                }}
              ></TextContentWrapper>
            )}
          </div>
        </div>
      </dl>
    </div>
  );
};

interface DeleteRecipeProps {
  recipe: Pick<Recipe, "id" | "locked">;
  user: CurrentUser;
  setDeleted: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedRecipes?: Dispatch<SetStateAction<string[]>>;
}

const DeleteRecipe: React.FC<DeleteRecipeProps> = ({
  recipe,
  user,
  setDeleted,
  setSelectedRecipes,
}) => {
  const locked =
    isStudent(user) && (!user.school?.permModifyRecipes || recipe.locked);

  const [{ fetching, error }, deleteRecipe] = useDeleteRecipeMutation();

  if (error) return <ErrorMessage error={error} />;

  return locked ? null : (
    <UserContext.Consumer>
      {(user) => (
        <Tooltip content="Delete This Recipe">
          <button
            disabled={
              (isStudent(user) && !user.school?.permDeleteRecipes) || fetching
            }
            onClick={async () => {
              await deleteRecipe({ id: recipe.id });

              setDeleted(true);

              if (setSelectedRecipes) {
                setSelectedRecipes((prevSelected) =>
                  prevSelected.filter((id) => id !== recipe.id)
                );
              }
            }}
          >
            {fetching ? (
              <div className="spinner h-5 w-5" />
            ) : (
              <TrashIcon className="w-5 text-orange-600 hover:text-orange-700" />
            )}
          </button>
        </Tooltip>
      )}
    </UserContext.Consumer>
  );
};

export default RecipesListItem;
